import { useState } from "react";
import type maplibregl from "maplibre-gl";
import { Layers } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { MAP_COLORS } from "@/lib/map-colors";

const LAYER_OPTIONS = [
  { id: "streets", label: "Streets", color: MAP_COLORS.street },
  {
    id: "matched-streets",
    label: "Matched streets",
    color: MAP_COLORS.covered,
  },
  { id: "strava-runs", label: "Strava runs", color: MAP_COLORS.stravaRun },
] as const;

// Strava runs start hidden to match the layer's default `visibility: "none"`.
const DEFAULT_VISIBILITY: Record<string, boolean> = {
  streets: true,
  "matched-streets": true,
  "strava-runs": false,
};

interface LayersMenuProps {
  map: maplibregl.Map | null;
}

// Popover of per-layer toggles, opened from the layers button in the map's
// bottom-left control stack. Drives each layer's visibility on the map.
export const LayersMenu = ({ map }: LayersMenuProps) => {
  const [visibility, setVisibility] =
    useState<Record<string, boolean>>(DEFAULT_VISIBILITY);

  const toggle = (id: string) => {
    const next = !visibility[id];
    setVisibility((prev) => ({ ...prev, [id]: next }));
    if (map?.getLayer(id)) {
      map.setLayoutProperty(id, "visibility", next ? "visible" : "none");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Layers"
          aria-label="Toggle map layers"
          // `!flex` overrides Mapbox's `.maplibregl-ctrl-group button { display:
          // block }` (higher specificity) so the icon actually centers; same for
          // the open-state tint over Mapbox's `background-color: initial`.
          className="!flex h-[29px] w-[29px] items-center justify-center text-foreground data-[state=open]:!bg-muted"
        >
          <Layers className="h-[18px] w-[18px]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="right"
        align="end"
        sideOffset={8}
        className="w-56"
      >
        <DropdownMenuLabel>Layers</DropdownMenuLabel>
        {LAYER_OPTIONS.map((opt) => (
          <label
            key={opt.id}
            className="flex cursor-pointer items-center justify-between gap-3 rounded-sm px-2 py-2 hover:bg-muted"
          >
            <span className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: opt.color }}
              />
              <span className="text-sm">{opt.label}</span>
            </span>
            <Switch
              checked={visibility[opt.id]}
              onCheckedChange={() => toggle(opt.id)}
            />
          </label>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
